import { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const UNIT = { gram: 'q', ml: 'ml', eded: 'əd', kg: 'kq', litr: 'lt' };

export default function SayimKassir() {
    const [rows, setRows]         = useState([]);
    const [loading, setLoading]   = useState(true);
    const [sessions, setSessions] = useState([]);   // paralel anbar sessiyaları [{depot_id, depot_name, status}]
    const [selDepot, setSelDepot] = useState(null);  // hazırda doldurulan aktiv anbar (0=hamısı, >0 depo, null=yox)
    const [draft, setDraft]       = useState({});    // ingredient_id -> string
    const [search, setSearch]     = useState('');
    const [reqDepot, setReqDepot] = useState('');    // icazə istəmək üçün seçilən anbar ('' = bütün depolar)
    const [saving, setSaving]     = useState(false);
    const [busy, setBusy]         = useState(false);
    const [syncing, setSyncing]   = useState(false);
    const [autoSaved, setAutoSaved] = useState(false);
    const draftRef = useRef({});
    const autoSaveTimer = useRef(null);
    const selDepotRef = useRef(null);
    useEffect(() => { selDepotRef.current = selDepot; }, [selDepot]);

    // aktiv anbar varsa və seçim yoxdursa/artıq aktiv deyilsə → ilk aktivi seç
    const pickDefaultDepot = (list) => {
        const act = list.filter(s => s.status === 'active');
        setSelDepot(prev => {
            if (prev !== null && act.some(s => Number(s.depot_id) === Number(prev))) return prev;
            return act.length ? Number(act[0].depot_id) : null;
        });
    };

    const loadDraft = async () => {
        try {
            const d = await api.get('/sayim/draft');
            const map = {};
            for (const [k, v] of Object.entries(d.data?.data || {})) map[k] = String(v);
            setDraft(map); draftRef.current = map;
        } catch { /* ignore */ }
    };

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [rep, ses] = await Promise.all([
                api.get('/sayim/report'),
                api.get('/sayim/session'),
            ]);
            setRows(rep.data?.data || []);
            const list = ses.data?.sessions || [];
            setSessions(list);
            pickDefaultDepot(list);
            if (list.some(s => s.status === 'active')) await loadDraft();
            else { setDraft({}); draftRef.current = {}; }
        } catch { toast.error('Sayım yüklənmədi.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { draftRef.current = draft; }, [draft]);

    // hər 15s sessiyaları yoxla (admin təsdiq/bitir dəyişikliyini tut)
    useEffect(() => {
        const key = (a) => a.map(s => `${s.depot_id}:${s.status}`).sort().join('|');
        const t = setInterval(async () => {
            try {
                const ses = await api.get('/sayim/session');
                const list = ses.data?.sessions || [];
                setSessions(prev => { if (key(prev) !== key(list)) loadAll(); return list; });
            } catch { /* ignore */ }
        }, 15000);
        return () => clearInterval(t);
    }, [loadAll]);

    const syncNow = async () => {
        setSyncing(true);
        try {
            const [rep, ses] = await Promise.all([
                api.get('/sayim/report'),
                api.get('/sayim/session'),
            ]);
            setRows(rep.data?.data || []);
            const list = ses.data?.sessions || [];
            setSessions(list);
            pickDefaultDepot(list);
            if (list.some(s => s.status === 'active')) await loadDraft();
            toast.success('Sinxronlaşdı (admin dəyişiklikləri çəkildi).');
        } catch { toast.error('Sync alınmadı.'); }
        finally { setSyncing(false); }
    };

    const requestAccess = async () => {
        setBusy(true);
        try {
            await api.post('/sayim/session/request', { depot_id: Number(reqDepot) || 0 });
            toast.success('İcazə istəyi göndərildi. Admin təsdiqini gözləyin.');
            setReqDepot('');
            loadAll();
        } catch (e) { toast.error(e.response?.data?.msg || 'Xəta.'); }
        finally { setBusy(false); }
    };

    // seçilmiş anbarın (0=hamısı) məhsulları + draft item-ləri
    const scopeRows = (depId) => rows.filter(r => Number(depId) === 0 || r.depot_id === Number(depId));
    const draftItemsFor = (depId) => scopeRows(depId).map(r => ({ ingredient_id: r.id, amount: draftRef.current[r.id] ?? '' }));

    const saveDraft = async () => {
        if (selDepot === null) return;
        setSaving(true);
        try {
            await api.put('/sayim/draft', { depot_id: Number(selDepot) || 0, items: draftItemsFor(selDepot) });
            toast.success('Yadda saxlanıldı. Admin "Bitir" edənə qədər redaktə edə bilərsiniz.');
        } catch (e) { toast.error(e.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    // avto-saxlama (fonda, debounce 800ms)
    const scheduleAutoSave = () => {
        const dep = selDepotRef.current;
        if (dep === null) return;
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(async () => {
            try {
                await api.put('/sayim/draft', { depot_id: Number(dep) || 0, items: draftItemsFor(dep) });
                setAutoSaved(true);
                setTimeout(() => setAutoSaved(false), 1500);
            } catch { /* səssiz — manual "Yadda saxla" var */ }
        }, 800);
    };

    const handleDraftChange = (id, val) => {
        setDraft((p) => { const next = { ...p, [id]: val }; draftRef.current = next; return next; });
        scheduleAutoSave();
    };

    // ── derived ──
    const activeSessions    = sessions.filter(s => s.status === 'active');
    const requestedSessions = sessions.filter(s => s.status === 'requested');
    const curSession = sessions.find(s => Number(s.depot_id) === Number(selDepot)) || null;
    const active     = curSession?.status === 'active';
    const curDepotName = curSession ? (Number(curSession.depot_id) === 0 ? 'Bütün depolar' : curSession.depot_name) : '';

    // icazə istəmək üçün mövcud depolar (artıq istənilməyənlər)
    const usedDepotIds = new Set(sessions.map(s => Number(s.depot_id)));
    const depotOptions = Array.from(
        rows.reduce((m, r) => { if (r.depot_id) m.set(r.depot_id, r.depot_name || 'Depo'); return m; }, new Map())
    ).map(([id, name]) => ({ id: String(id), name })).filter(d => !usedDepotIds.has(Number(d.id)));
    const allUsed = usedDepotIds.has(0);

    const scopeId  = (active && Number(selDepot) > 0) ? Number(selDepot) : null;
    const filtered = rows.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase()) &&
        (!scopeId || r.depot_id === scopeId)
    );
    const filledCount = scopeRows(selDepot ?? 0).filter(r => {
        const v = draft[r.id]; return v !== '' && v !== null && v !== undefined;
    }).length;

    const printSheet = async () => {
        const dateStr = new Date().toLocaleString('az-AZ', {
            timeZone: 'Asia/Baku', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
        const depId = Number(selDepot) > 0 ? Number(selDepot) : null;
        const sheetRows = depId ? rows.filter(r => r.depot_id === depId) : rows;
        const body = sheetRows.map((r, i) => `<tr style="background:${i % 2 ? '#f8fafc' : '#fff'}">
            <td style="font-weight:600">${r.name}</td>
            <td style="text-align:center;color:#888">${UNIT[r.unit] || r.unit}</td>
            <td></td></tr>`).join('');
        const heading = depId ? (curDepotName || 'Sayım vərəqi') : 'Sayım vərəqi';
        const html = `<!doctype html><html lang="az"><head><meta charset="utf-8"><title>Sayım vərəqi</title>
            <style>
              *{box-sizing:border-box} body{font-family:Arial,sans-serif;padding:16px;color:#111}
              h2{margin:0 0 2px} .sub{font-size:11px;color:#555;margin-bottom:10px}
              table{width:100%;border-collapse:collapse;font-size:12px}
              th,td{border:1px solid #cbd5e1;padding:5px 7px}
              th{background:#1e293b;color:#fff;font-size:11px}
            </style></head><body>
            <h2>${heading}</h2>
            <div class="sub">${dateStr}</div>
            <table><thead><tr>
              <th>Məhsul</th><th style="width:55px">Vahid</th>
              <th style="width:200px">Sayılan (əl ilə)</th>
            </tr></thead><tbody>${body}</tbody></table>
            </body></html>`;
        try {
            const { data } = await api.get('/printers');
            const a4 = (data?.data || []).find(p => p.printer_type === 'a4' && Number(p.active) === 1);
            if (window.chrome?.webview?.postMessage && a4) {
                window.chrome.webview.postMessage({ type: 'print-a4', printer: a4.name, html });
                toast.success('Sayım vərəqi çapa göndərildi.');
                return;
            }
        } catch { /* fallback */ }
        const win = window.open('', '_blank');
        if (!win) { toast.error('Pop-up bloklandı — brauzerdə icazə verin.'); return; }
        win.document.write(html); win.document.close();
        setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 400);
    };

    const depLabel = (s) => Number(s.depot_id) === 0 ? 'Bütün depolar' : (s.depot_name || `Depo ${s.depot_id}`);

    return (
        <Layout>
            <div className="mx-auto max-w-4xl p-3 sm:p-4">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <h1 className="text-lg font-black text-slate-800">📋 Sayım</h1>
                    <div className="flex flex-wrap items-center gap-2">
                        <button onClick={syncNow} disabled={syncing}
                            className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                            title="Admin dəyişiklikləri ilə sinxronla">
                            {syncing ? '...' : '🔄 Sync'}
                        </button>
                        {active && (
                            <button onClick={printSheet}
                                className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-900"
                                title={`${curDepotName} sayım vərəqi`}>
                                🖨️ Sayım vərəqi{curDepotName ? ` — ${curDepotName}` : ''}
                            </button>
                        )}
                    </div>
                </div>

                {/* İcazə istə (istənilən vaxt, bir neçə anbar üçün) */}
                <div className="mb-3 rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-slate-600">Anbar seçib admin təsdiqi istə (paralel — bir neçə anbara icazə ala bilərsiniz):</div>
                        <div className="flex flex-wrap items-center gap-2">
                            <select value={reqDepot} onChange={(e) => setReqDepot(e.target.value)}
                                className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 focus:border-blue-400 focus:outline-none">
                                {!allUsed && <option value="">🏬 Bütün depolar</option>}
                                {depotOptions.map((d) => <option key={d.id} value={d.id}>🏪 {d.name}</option>)}
                            </select>
                            <button onClick={requestAccess} disabled={busy || (allUsed && depotOptions.length === 0)}
                                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">
                                {busy ? '...' : '🔑 İcazə istə'}
                            </button>
                        </div>
                    </div>

                    {/* Gözləyən anbarlar */}
                    {requestedSessions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {requestedSessions.map(s => (
                                <span key={s.depot_id} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-700">
                                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                                    {depLabel(s)} — gözləyir
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Aktiv anbarlar — seç və doldur */}
                    {activeSessions.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5">
                            <span className="text-sm font-semibold text-emerald-800">✅ Aktiv anbar:</span>
                            <select value={selDepot ?? ''} onChange={(e) => setSelDepot(Number(e.target.value))}
                                className="rounded-lg border-2 border-emerald-200 px-3 py-1.5 text-sm font-bold text-emerald-800 bg-white focus:outline-none">
                                {activeSessions.map(s => (
                                    <option key={s.depot_id} value={Number(s.depot_id)}>🏪 {depLabel(s)}</option>
                                ))}
                            </select>
                            <span className="text-xs text-emerald-600">— dəyərləri yaz (avtomatik saxlanır)</span>
                            <div className="ml-auto flex items-center gap-2">
                                <span className={`text-[11px] font-semibold transition-opacity ${autoSaved ? 'text-emerald-600 opacity-100' : 'opacity-0'}`}>✓ avto-saxlanıldı</span>
                                <button onClick={saveDraft} disabled={saving}
                                    className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                                    {saving ? 'Saxlanır...' : `💾 Yadda saxla (${filledCount})`}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeSessions.length === 0 && requestedSessions.length === 0 && (
                        <p className="mt-3 text-xs text-slate-400">Hələ aktiv/gözləyən sayım yoxdur. Yuxarıdan anbar seçib icazə istəyin.</p>
                    )}
                </div>

                {/* Axtarış */}
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="🔍 Məhsul axtar..."
                    className="mb-3 w-full rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-blue-400 focus:outline-none" />

                {/* Cədvəl */}
                <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                    <table className="w-full min-w-[460px] text-sm">
                        <thead>
                            <tr className="border-b border-slate-100 bg-slate-50 text-[11px] uppercase text-slate-400">
                                <th className="px-3 py-2.5 text-left font-bold">Məhsul</th>
                                <th className="px-2 py-2.5 text-center font-bold">Vahid</th>
                                <th className="px-3 py-2.5 text-right font-bold">Sayılan</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={3} className="px-3 py-10 text-center text-slate-400">Yüklənir...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={3} className="px-3 py-10 text-center text-slate-400">Məhsul yoxdur.</td></tr>
                            ) : filtered.map((r) => (
                                <tr key={r.id} className="border-t border-slate-50">
                                    <td className="px-3 py-2 font-semibold text-slate-800">{r.name}</td>
                                    <td className="px-2 py-2 text-center text-slate-400">{UNIT[r.unit] || r.unit}</td>
                                    <td className="px-3 py-2 text-right">
                                        <input type="number" step="0.001" inputMode="decimal"
                                            disabled={!active}
                                            value={draft[r.id] ?? ''}
                                            onChange={(e) => handleDraftChange(r.id, e.target.value)}
                                            placeholder={active ? '0' : '🔒'}
                                            className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-right text-sm font-mono focus:border-emerald-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-300" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {active && (
                    <p className="mt-3 text-center text-[11px] text-slate-400">
                        Dəyərləri yazdıqdan sonra "Yadda saxla" basın. Yekunu admin "Bitir" ilə təsdiqləyəcək.
                    </p>
                )}
            </div>
        </Layout>
    );
}
